import React from "react";
import { Box } from "@mui/joy";
import type { Card, Cards, Training } from "../../../../api/training/Training.typed";
import { TrainingStatusCode } from "../../../../api/training/Training.typed";
import type { ZekayCustomer } from "../../../../api/customers/Customer.typed";
import { useCustomer, useTraining } from "../../../../contexts/api";
import { useAlert } from "../../../../contexts/app/notificationToast";
import { useConfirmationModal } from "../../../../contexts/app/confirmation";
import useAppSettings from "../../../../contexts/app/appSettings";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useAuth } from "../../../../contexts/api";
import { userDisplayName } from "../../../../utils/userHelper";
import { usePermissions } from "../../../../contexts/app/permissions";
import { Permission } from "../../../../api/zekay/permissions";
import ProfileForm from "../../../shared/profileForm/ProfileForm";
import CommentsModal from "../../../common/CommentsModal";
import DatesComponent, { areAllDatesValid } from "../../../common/DatesComponent";
import DocumentsComponent, { getDocumentsBadgeStatus } from "../../../common/DocumentsComponent";
import DarkCard from "../../../common/DarkCard";
import DarkTypography from "../../../common/DarkTypography";
import TrainingIdentityBlock from "../../common/TrainingIdentityBlock";
import {
  trainingActionSlotSpacerSx,
  trainingCardActionSlotSx,
  trainingMetaActionsSlotSx,
} from "../../common/trainingCardActionsSx";
import { SDate, SFile, SComment, SQuiz, SSendMail } from "../../../../styles/StyledMUI";
import CardOrganisationStatus from "./cardOrganisationStatus";
import DetailsTooltip from "../../../common/DetailsTooltip";
import TheoryTestPanel from "../../../shared/theoryTest/TheoryTestPanel";
import {
  hasTheoryTestHistory,
  isTheoryTestValidated,
} from "../../../shared/theoryTest/theoryTestHelpers";
import FollowUpSheetAction from "../../../shared/followup/FollowUpSheetAction";
import type { FollowUpSheet } from "../../../../api/followup/Followup.typed";
import { normalizeMultiCommentList } from "../../../../api/zekay/comments";
import type { MultiComment } from "../../../../api/zekay/comments";
import ListRowAlertBanner from "../../../common/ListRowAlertBanner";
import { trainingAlertLabels } from "../../common/trainingAlerts";
import {
  cardValidationItemActionsClusterSx,
  cardValidationItemCardsCenterOuterSx,
  cardValidationItemCardsChipClusterSx,
  cardValidationItemCardsColumnSx,
  cardValidationItemIdentityColumnSx,
  cardValidationItemRowSx,
  colorTokens,
  listRowInactivityAlertSx,
  trainingRowHighlightBgSx,
} from "../../../../styles/globalStyles";
import TrainingListRowGuard from "../../common/TrainingListRowGuard";
import TrainingRowTooltipButton from "../../common/TrainingRowTooltipButton";
import { useTrainingCustomerProfile } from "../../common/hooks/useTrainingCustomerProfile";
import { useTrainingMail } from "../../common/hooks/useTrainingMail";
import TrainingStatusActionSelect from "../../common/TrainingStatusActionSelect";
import type { TrainingStatusAction } from "../../common/trainingStatusActionTokens";
import { trainingStatusMenuActions } from "../../common/trainingStatusMenuActions";

type CardValidationItemProps = {
  training: Training;
  initialCustomer?: ZekayCustomer;
  setTrainings: React.Dispatch<React.SetStateAction<Training[] | undefined>>;
  refreshTrainings: () => Promise<Training[]>;
  updateTrainingLocally: (training: Training) => void;
  highlighted?: boolean;
};

export default function CardValidationItem(props: CardValidationItemProps) {
  const {
    training,
    initialCustomer,
    setTrainings,
    refreshTrainings,
    updateTrainingLocally,
    highlighted,
  } = props;
  const placeholder = (
    <DarkTypography level="h4" colorVariant="muted">
      —
    </DarkTypography>
  );
  const { getCustomer } = useCustomer();
  const { sendTrainingInProgressMail } = useTrainingMail();
  const { validateUserQualification, cancelTraining, updateTraining, isTheoryTestOnline } =
    useTraining();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmationModal();
  const { config } = useAppSettings();
  const trainingStatusMenuActionList = React.useMemo(
    () => trainingStatusMenuActions(config?.CustomersSync === true, "cardValidation"),
    [config?.CustomersSync],
  );
  const { customer, setCustomer, profileFormOpen, setProfileFormOpen, openProfile } =
    useTrainingCustomerProfile({
      training,
      initialCustomer,
      getCustomer,
      showAlert,
    });
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.TRAININGS_EDIT);
  const canEditProfile = hasPermission(Permission.CUSTOMERS_PROFILE_EDIT);
  const isDesync = config?.CustomersSync === false;
  const [multiComments, setMultiComments] = React.useState<MultiComment[] | undefined>(() =>
    normalizeMultiCommentList(training.MultiComments as unknown[] | undefined),
  );
  const [testResult, setTestResult] = React.useState(training.TestResult || "");
  const [commentsModalOpen, setCommentsModalOpen] = React.useState(false);
  const [isOpenDates, setIsOpenDates] = React.useState(false);
  const [isOpenDocuments, setIsOpenDocuments] = React.useState(false);
  const [cards, setCards] = React.useState<Card[]>(() =>
    (training.Cards?.Card ?? []).map((c) => ({
      Organisation: c.Organisation,
      Status: c.Status ?? false,
    })),
  );
  const [theory, setTheory] = React.useState<Dayjs | null>(
    training.Theory && training.Theory.trim() !== "" ? dayjs(training.Theory) : null,
  );

  const [rifap, setRifap] = React.useState<Dayjs | null>(
    training.RifapDate && training.RifapDate.trim() !== "" ? dayjs(training.RifapDate) : null,
  );

  const [isOpenTheoryTests, setIsOpenTheoryTests] = React.useState(false);

  const showTheoryTest =
    isTheoryTestOnline(training.TrainingTemplateID) ||
    hasTheoryTestHistory(testResult, training.TheoryTests);

  React.useEffect(() => {
    setMultiComments(normalizeMultiCommentList(training.MultiComments as unknown[] | undefined));
  }, [training.MultiComments]);

  React.useEffect(() => {
    setCards(
      (training.Cards?.Card ?? []).map((c) => ({
        Organisation: c.Organisation,
        Status: c.Status ?? false,
      })),
    );
  }, [training.Cards?.Card]);

  const hasCustomerEmail = (customer?.MailAddress ?? "").trim() !== "";
  const docsStatus = React.useMemo(
    () => getDocumentsBadgeStatus(training, customer),
    [training, customer],
  );

  const alertLabels = trainingAlertLabels(training);
  const hasAlert = alertLabels.length > 0;

  const handleCommentsSave = async (updated?: MultiComment[]) => {
    const next = updated ?? multiComments;
    const { UpdatedAt: _updatedAt, ...trainingPayload } = training;
    const updatedTraining = {
      ...trainingPayload,
      MultiComments: next,
      TestResult: testResult,
      UpdatedBy: userDisplayName(user),
    };
    try {
      setMultiComments(next);
      await updateTraining(updatedTraining);
    } catch (error) {
      showAlert("Erreur lors de la mise à jour des commentaires", "danger");
    }
  };

  const handleCommentsModalClose = () => {
    setCommentsModalOpen(false);
    updateTrainingLocally({ ...training, MultiComments: multiComments });
  };

  const handleStatusAction = async (action: TrainingStatusAction) => {
    if (action === "terminer") {
      return;
    }

    const { UpdatedAt: _updatedAt, ...trainingPayload } = training;

    if (action === "valider") {
      const ok = await showConfirmation(
        "Valider la qualification",
        "Êtes-vous sûr de vouloir valider cette qualification ?",
      );
      if (!ok) return;
      const validatedTraining = {
        ...trainingPayload,
        Status: "Validé",
        UpdatedBy: userDisplayName(user),
      };
      await validateUserQualification(validatedTraining, { skipConfirmation: true });
      await refreshTrainings();
      return;
    }

    if (action === "annuler") {
      const ok = await showConfirmation(
        "Annuler la formation",
        "Confirmer l'annulation de cette formation ?",
      );
      if (!ok) return;
      await cancelTraining(
        {
          ...trainingPayload,
          UpdatedBy: userDisplayName(user),
        },
        { skipConfirmation: true },
      );
      await refreshTrainings();
      return;
    }

    if (action === "en_cours") {
      const ok = await showConfirmation(
        "Remettre en cours",
        "Repasser la formation en « En cours » ?",
      );
      if (!ok) return;
      try {
        await updateTraining({
          ...trainingPayload,
          Status: "En cours",
          UpdatedBy: userDisplayName(user),
        });
        await refreshTrainings();
      } catch (error) {
        showAlert("Erreur lors de la mise à jour du statut", "danger");
      }
    }
  };

  const handleSave = async (cardsToSave?: Card[]) => {
    const cardsToUse = cardsToSave ?? cards;
    const cs: Cards = {
      Card: cardsToUse.map((c) => ({
        Organisation: c.Organisation,
        Status: c.Status ?? false,
      })),
    };
    const { UpdatedAt: _updatedAt, ...trainingPayload } = training;
    const tr: Training = {
      ...trainingPayload,
      MultiComments: multiComments,
      Cards: cs,
      UpdatedBy: userDisplayName(user),
    };
    await updateTraining(tr);
    setTrainings((prev) => {
      if (!prev) return prev;
      return prev.map((t) => (t.ID === training.ID ? { ...t, Cards: cs } : t));
    });
  };

  const handleSendMail = () => sendTrainingInProgressMail(training);

  const handleFollowUpSaved = React.useCallback(
    (sheet: FollowUpSheet) => updateTrainingLocally({ ...training, FollowUpSheet: sheet }),
    [training, updateTrainingLocally],
  );

  const getTrainingsByTemplateIdWrapper = async (
    _templateId: string,
  ): Promise<Training[] | undefined> => {
    return refreshTrainings();
  };

  return (
    <>
      <DarkCard
        dense
        sx={{
          mb: 1.5,
          ...(hasAlert ? listRowInactivityAlertSx : {}),
          ...(!hasAlert && highlighted ? trainingRowHighlightBgSx : {}),
        }}
      >
        <ListRowAlertBanner labels={alertLabels} />
        <Box sx={cardValidationItemRowSx}>
          <Box sx={cardValidationItemIdentityColumnSx}>
            <TrainingIdentityBlock
              training={training}
              canOpenProfile={canEdit}
              onOpenProfile={openProfile}
              fallback={placeholder}
            />
          </Box>

          <Box sx={cardValidationItemCardsColumnSx}>
            {canEdit && cards.length > 0 ? (
              <Box sx={cardValidationItemCardsCenterOuterSx}>
                <Box component="span" sx={cardValidationItemCardsChipClusterSx}>
                  {cards.map((c: Card, index: number) => (
                    <CardOrganisationStatus
                      key={`${c.Organisation}-${index}`}
                      index={index}
                      cardOrganisation={c.Organisation}
                      cardStatus={c.Status ?? false}
                      cards={cards}
                      setCards={setCards}
                      handleSave={handleSave}
                    />
                  ))}
                </Box>
              </Box>
            ) : !canEdit ? (
              placeholder
            ) : (
              <Box sx={trainingActionSlotSpacerSx} />
            )}
          </Box>

          <Box sx={cardValidationItemActionsClusterSx}>
            <Box sx={trainingCardActionSlotSx}>
              <TrainingListRowGuard enabled={canEdit} fallback={placeholder}>
                <TrainingRowTooltipButton
                  title="Commentaires"
                  ariaLabel="Commentaires"
                  icon={<SComment />}
                  onClick={() => setCommentsModalOpen(true)}
                  ctaVariant="accent"
                  badgeContent={multiComments?.length ?? 0}
                  badgeColor="neutral"
                />
              </TrainingListRowGuard>
            </Box>
            <Box sx={trainingCardActionSlotSx}>
              {canEdit && (training.NeedsTheory || training.NeedsRifap) ? (
                <TrainingRowTooltipButton
                  title="Sessions théoriques"
                  ariaLabel="Sessions théoriques"
                  icon={<SDate />}
                  onClick={() => setIsOpenDates(!isOpenDates)}
                  ctaVariant="accent"
                  badgeContent={areAllDatesValid(training, theory, rifap) ? "✓" : 0}
                  badgeColor="success"
                />
              ) : !canEdit ? (
                placeholder
              ) : (
                <Box sx={trainingActionSlotSpacerSx} />
              )}
            </Box>
            <Box sx={trainingCardActionSlotSx}>
              <TrainingListRowGuard enabled={canEdit} fallback={placeholder}>
                <TrainingRowTooltipButton
                  title="Docs"
                  ariaLabel="Documents"
                  icon={<SFile />}
                  onClick={() => setIsOpenDocuments(!isOpenDocuments)}
                  ctaVariant="accent"
                  badgeContent={
                    docsStatus.allUploaded ? "✓" : `${docsStatus.uploaded}/${docsStatus.total}`
                  }
                  badgeColor={docsStatus.allUploaded ? "success" : "neutral"}
                />
              </TrainingListRowGuard>
            </Box>
            <Box sx={trainingCardActionSlotSx}>
              {canEdit && showTheoryTest ? (
                <TrainingRowTooltipButton
                  title="QCM"
                  ariaLabel="QCM"
                  icon={<SQuiz />}
                  onClick={() => setIsOpenTheoryTests(!isOpenTheoryTests)}
                  ctaVariant="accent"
                  badgeContent={isTheoryTestValidated(testResult, training.TheoryTests) ? "✓" : 0}
                  badgeColor="success"
                />
              ) : !canEdit ? (
                placeholder
              ) : (
                <Box sx={trainingActionSlotSpacerSx} />
              )}
            </Box>
            <Box sx={trainingCardActionSlotSx}>
              <FollowUpSheetAction
                training={training}
                status={TrainingStatusCode.Done}
                customerName={[training.FirstName, training.LastName].filter(Boolean).join(" ")}
                onSaved={handleFollowUpSaved}
                fallback={placeholder}
              />
            </Box>
            <Box sx={trainingCardActionSlotSx}>
              {canEdit ? (
                <TrainingRowTooltipButton
                  title={hasCustomerEmail ? "Mail" : "Email client manquant"}
                  ariaLabel={hasCustomerEmail ? "Mail" : "Mail indisponible"}
                  icon={<SSendMail />}
                  onClick={hasCustomerEmail ? handleSendMail : undefined}
                  disabled={!hasCustomerEmail}
                  ctaVariant="accent"
                />
              ) : (
                placeholder
              )}
            </Box>
            <Box sx={trainingCardActionSlotSx}>
              {canEdit ? (
                <TrainingStatusActionSelect
                  listKey={training.ID ?? training.CustomerID ?? 0}
                  allowedActions={trainingStatusMenuActionList}
                  onAction={handleStatusAction}
                />
              ) : (
                placeholder
              )}
            </Box>
            <Box sx={trainingMetaActionsSlotSx}>
              <DetailsTooltip training={training} />
            </Box>
          </Box>
        </Box>

        {isOpenDocuments && (
          <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
            <DocumentsComponent
              training={training}
              customer={customer}
              setTrainings={setTrainings}
              refreshTrainings={refreshTrainings}
              standalone
              onCustomerUpdated={setCustomer}
            />
          </Box>
        )}
        <CommentsModal
          open={commentsModalOpen}
          title="Commentaires"
          currentUserName={userDisplayName(user)}
          multiComments={multiComments}
          setMultiComments={setMultiComments}
          onSave={handleCommentsSave}
          onClose={handleCommentsModalClose}
          allowEdit={canEdit}
        />
        {isOpenDates && (
          <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
            <DatesComponent
              training={training}
              theory={theory}
              rifap={rifap}
              setTheory={setTheory}
              setRifap={setRifap}
              updateTraining={updateTraining}
              getTrainingsByTemplateId={getTrainingsByTemplateIdWrapper}
              setTrainings={setTrainings}
              isOpenDates={isOpenDates}
              status={2}
              standalone
            />
          </Box>
        )}
        {isOpenTheoryTests && showTheoryTest && (
          <Box sx={{ borderTop: `1px solid ${colorTokens.border.default}`, mt: 1.5, pt: 1.5 }}>
            <TheoryTestPanel
              training={training}
              testResult={testResult}
              setTestResult={setTestResult}
              isOpenTheoryTests={isOpenTheoryTests}
              standalone
            />
          </Box>
        )}
      </DarkCard>

      {/* Modals */}
      {profileFormOpen && (
        <ProfileForm
          customer={customer}
          setProfileFormOpen={setProfileFormOpen}
          setCustomerUpdated={setCustomer}
          editable={canEditProfile && isDesync}
          desync={isDesync}
        />
      )}
    </>
  );
}
